import { memo } from "react"
import { Box, Stack, Switch, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedEnum, LocalizedStrict } from "../../../../../../localization/components/AppLocalized"

import { useWeekMenu } from "../../WeekMenuContext"
import { WeekDayPublishStatus, WeekMenuDayState } from "../../weekMenuTypes"
import { PlannerTag, PlannerTagVariant } from "../PlannerTag"
import { DayOverflowMenu } from "./DayOverflowMenu"

const useStyles = makeStyles()((theme) => ({
    head: {
        position: "relative",
        padding: ".75rem .5rem",
        textAlign: "center",
        background: theme.palette.background.paper,
        borderLeft: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    dayName: {
        color: theme.palette.tailwind.slate[600],
        textTransform: "uppercase",
    },
    dayDate: {
        color: theme.palette.tailwind.slate[400],
    },
    publishSwitch: {
        "& .MuiSwitch-switchBase": {
            color: theme.palette.tailwind.neutral[50],
        },
        "& .MuiSwitch-switchBase + .MuiSwitch-track": {
            backgroundColor: theme.palette.tailwind.slate[500],
        },
        "& .MuiSwitch-switchBase.Mui-checked": {
            color: theme.palette.tailwind.green[600],
        },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: theme.palette.tailwind.green[600],
            opacity: 0.5,
        },
    },
    status: {
        fontSize: ".625rem",
        fontWeight: 500,
    },
    statusPublished: {
        color: theme.palette.tailwind.green[600],
    },
    statusDraft: {
        color: theme.palette.tailwind.slate[500],
    },
    statusUpdated: {
        color: theme.palette.primary.main,
    },
}))

type Props = Readonly<{
    dayIndex: number
    dayName: string
    dayDate: string
    dayState: WeekMenuDayState
    filledCount: number
    totalCount: number
    onMakeTheme: () => void
    onRenameTheme: () => void
    onRemoveTheme: () => void
    onCloseDay: () => void
}>

export const WeekDayHeader = memo(function WeekDayHeader({
    dayIndex,
    dayName,
    dayDate,
    dayState,
    filledCount,
    totalCount,
    onMakeTheme,
    onRenameTheme,
    onRemoveTheme,
    onCloseDay,
}: Props) {
    const { classes, cx } = useStyles()
    const { actions } = useWeekMenu()

    const statusClasses: Record<WeekDayPublishStatus, string> = {
        [WeekDayPublishStatus.PUBLISHED]: classes.statusPublished,
        [WeekDayPublishStatus.UPDATED]: classes.statusUpdated,
        [WeekDayPublishStatus.DRAFT]: classes.statusDraft,
    }

    return (
        <Box className={classes.head}>
            <Stack spacing=".5rem">
                <Stack direction="row" alignItems="baseline" justifyContent="center" spacing=".375rem">
                    <Typography variant="subtitle2" className={classes.dayName}>
                        {dayName}
                    </Typography>
                    <Typography variant="caption" className={classes.dayDate}>
                        {dayDate}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing=".75rem">
                    <Stack direction="row" alignItems="center" spacing=".375rem">
                        <LocalizedStrict id="merchant-week-menu-day-publish-tooltip" attrs={{ title: true }}>
                            <Tooltip arrow title="Publish this day to customers">
                                <Switch
                                    size="small"
                                    className={classes.publishSwitch}
                                    checked={dayState.isPublishEnabled}
                                    onChange={(event) => actions.setDayPublishEnabled(dayIndex, event.target.checked)}
                                />
                            </Tooltip>
                        </LocalizedStrict>
                        <LocalizedEnum base="merchant-week-menu-day-status" enumValue={dayState.status}>
                            <Typography
                                variant="caption"
                                className={cx(classes.status, statusClasses[dayState.status])}
                            >
                                {dayState.status}
                            </Typography>
                        </LocalizedEnum>
                    </Stack>
                    <PlannerTag variant={PlannerTagVariant.COUNT}>
                        {filledCount}/{totalCount}
                    </PlannerTag>
                </Stack>
            </Stack>
            <DayOverflowMenu
                hasTheme={dayState.theme !== null}
                isClosed={dayState.isClosed}
                onMakeTheme={onMakeTheme}
                onRenameTheme={onRenameTheme}
                onRemoveTheme={onRemoveTheme}
                onCloseDay={onCloseDay}
                onReopenDay={() => actions.setDayClosed(dayIndex, false)}
            />
        </Box>
    )
})
